import { cn } from '../lib/cn.js';

/**
 * Icon — every icon in the app. Pass a lucide-react component:
 *
 *   <Icon icon={Check} />            16px, inherits the text colour
 *   <Icon icon={Bell} size={20} />
 *   <Icon icon={Info} inline />      inside running text: centred on the x-height
 *
 * WHY THIS EXISTS. The prototype drew icons with an icon FONT whose glyphs sit entirely above the
 * text baseline, inside elements with no flex and the brand font's deep 0.32em descender — so
 * every icon rode 1.5–3px high. An SVG with `shrink-0`, placed as a flex item, is centred by its
 * container exactly; anything that holds an icon is `inline-flex items-center justify-center`.
 */
const SIZES = [12, 14, 16, 18, 20, 24];

export function Icon({ icon: Glyph, size = 16, inline = false, strokeWidth, className, label, ...rest }) {
  if (!Glyph) return null;
  if (import.meta.env.DEV && !SIZES.includes(size)) console.warn(`<Icon size={${size}}>: use one of ${SIZES.join(', ')}`);
  return (
    <Glyph
      width={size}
      height={size}
      strokeWidth={strokeWidth ?? (size <= 14 ? 2.25 : 2)}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? 'img' : undefined}
      focusable="false"
      className={cn('shrink-0', inline ? 'icon-inline' : 'block', className)}
      {...rest}
    />
  );
}
